import { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import clsx from 'clsx'

import {
  useChatScopes,
  useChatThread,
  useChatThreads,
  useSendChatMessage,
  type ChatMessage,
  type ChatScope,
  type ChatScopeId,
  type ChatSource,
  type ChatThread,
  type ChatTier,
} from '../../lib/data'
import { messageCountLine, scopeSizeLine, startedAtClock } from '../../components/chat/labels'
import { AnswerBody } from '../../components/chat/AnswerBody'
import { CHAT_INPUT_MAX_PX, fitToText } from '../../lib/fit-textarea'
import { MCheck, MChevronDown, MChevronLeft, MMenu, MPlus, MSend } from '../shell/icons'
import { Eyebrow, SCROLL_BOTTOM, Skeleton } from '../shell/parts'

// ============================================================================
// 1h · CHAT — docs/design/handoff-mobile/README.md §1h, SIMPLIFIED BY THE OWNER
// on 2026-09-12 from two phone screenshots.
//
// "One conversational engine over the user's own life, with sources."
//
// ── WHAT HE ASKED FOR, IN HIS WORDS. "The text that says seven entries 15
// lectures etc is not needed on the mobile screen." "I'm not clear about what the
// everything / journal little cards at the top actually do." "The chat should be
// simplified on mobile with just new chats and a menu that opens from the side …
// a little button on the top right that looks like a menu that opens up and shows
// new chat and the history of the other chats." So:
//
//   · /chat opens a NEW chat. The old threads live behind the ☰ button, top right,
//     in a drawer whose first row is New chat.
//   · The header is title + ☰. No scope pills, no size line.
//   · SCOPE IS ONE TAP AWAY, NOT GONE. The README's rule was "scope is never
//     hidden in a menu"; the owner overruled it for the phone, and the honest
//     compromise is that the composer's scope label is a button that opens a
//     picker, and the picker is where each scope's derived size line now lives —
//     "7 entries · 15 lectures …" beside the scope it describes, which is the one
//     place that sentence means something. The label always names the scope in
//     force, so a scoped answer is still an answer whose scope you can see.
//
// ── EVERY FACTUAL CLAIM CARRIES A SOURCE. `ChatMessage.sources` is not optional
// and an assistant message with an empty array is a bug the data layer's own test
// catches. This screen renders that bug LOUDLY rather than hiding it.
//
// ── ON STREAMING, HONESTLY. The engine behind `POST /api/chat/messages` does not
// stream: it retrieves, answers, and returns the whole message with its sources
// in one response. Nothing here fakes a token stream; the wait is stated in words.
//
// ── THE COMPOSER SITS ON THE TAB BAR. It used to pad its bottom by the tab bar's
// height on the assumption the bar overlays content; the bar is in-flow, so the
// composer floated mid-screen over a hole (owner's screenshot). `SCROLL_BOTTOM`
// is now the capture button's clearance alone — see shell/parts.tsx.
//
// THE HEADER HAS NO GRADIENT BAND — README §Header band: "Chat has no band."
// ============================================================================

export function MobileChat() {
  const { threadId } = useParams()
  const navigate = useNavigate()
  const threads = useChatThreads()
  const scopes = useChatScopes()
  const send = useSendChatMessage()

  // No `/chat/:threadId` is a NEW chat. The earlier threads are in the drawer.
  const activeId = threadId
  const detail = useChatThread(activeId)
  const thread = detail.data?.thread

  /** Scope is PER-THREAD state and persists with the thread; a re-scope applies to later messages only. */
  const [overrides, setOverrides] = useState<Record<string, ChatScopeId>>({})
  /**
   * THE SCOPE CHOSEN BEFORE ANY THREAD EXISTS YET. On a new chat there is no
   * `thread.id` to key `overrides` on; this holds the choice until the send that
   * creates the thread, the same way routes/Chat.tsx's `newScope` does on desktop.
   */
  const [pendingScopeId, setPendingScopeId] = useState<ChatScopeId | null>(null)
  const [tier, setTier] = useState<ChatTier>('quick')
  const [draft, setDraft] = useState('')
  const [menuOpen, setMenuOpen] = useState(false)
  const [scopeOpen, setScopeOpen] = useState(false)
  const bottom = useRef<HTMLDivElement>(null)

  const scopeId: ChatScopeId = thread
    ? (overrides[thread.id] ?? thread.scopeId)
    : (pendingScopeId ?? 'everything')
  const scope = scopes.data?.find((s) => s.id === scopeId)

  // THE QUESTION ON ITS WAY, drawn as a sent bubble until the answer replaces it (owner,
  // 2026-09-23). Held past the mutation by one render so a NEW thread's route can follow the
  // answer without the empty state flashing in between.
  const [asking, setAsking] = useState<string | null>(null)

  useEffect(() => {
    bottom.current?.scrollIntoView({ block: 'end' })
  }, [detail.data?.messages.length, send.isPending, asking])

  function setScope(id: ChatScopeId) {
    if (thread) setOverrides((prev) => ({ ...prev, [thread.id]: id }))
    else setPendingScopeId(id)
  }

  // The field shrinks back when the draft is cleared by a send; React only re-renders the value.
  const field = useRef<HTMLTextAreaElement>(null)
  useEffect(() => {
    if (draft === '') fitToText(field.current, CHAT_INPUT_MAX_PX)
  }, [draft])

  function ask() {
    const text = draft.trim()
    if (!text) return
    // Out of the field the moment it is sent; back into it if the send is refused.
    setAsking(text)
    setDraft('')
    send.mutate(
      { threadId: activeId ?? null, text, scopeId, tier },
      {
        onError: () => {
          setAsking(null)
          setDraft((now) => (now.trim() === '' ? text : now))
        },
        onSuccess: (result) => {
          setAsking(null)
          setPendingScopeId(null)
          // The answer lands in a thread, so the route follows it there.
          navigate(`/chat/${result.thread.id}`, { replace: true })
        },
      },
    )
  }

  const isNew = !activeId

  return (
    <>
      {/* ── HEADER: title and the thread menu. Solid white, bottom border. ─── */}
      <div
        className="flex flex-none items-center gap-[10px] border-b border-line bg-surface px-[14px] pb-[10px] pt-2"
        style={{ paddingTop: 'calc(8px + env(safe-area-inset-top, 0px))' }}
      >
        <button
          type="button"
          aria-label="Back to Today"
          onClick={() => navigate('/')}
          className="flex size-11 flex-none items-center justify-center text-ink-2 active:bg-fill"
        >
          <MChevronLeft />
        </button>
        <div className="min-w-0 flex-1">
          <div className="truncate text-[15px] font-semibold leading-[1.2] text-ink">
            {isNew ? 'New chat' : (thread?.title ?? (detail.isPending ? '…' : 'Chat'))}
          </div>
          {thread && (
            <div className="mt-1 text-[10.5px] font-normal leading-none text-microcopy">
              started {startedAtClock(thread.startedAt)} · {messageCountLine(thread.messageCount)}
            </div>
          )}
        </div>
        <button
          type="button"
          aria-label="Chats"
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen(true)}
          className="flex size-11 flex-none items-center justify-center rounded-row text-ink-2 active:bg-fill"
        >
          <MMenu />
        </button>
      </div>

      {/* ── THE CONVERSATION ───────────────────────────────────────────────── */}
      <div className="flex min-h-0 flex-1 flex-col gap-[14px] overflow-y-auto px-[14px] pt-[14px]">
        {isNew && !asking && (
          // The new chat's only furniture: one sentence about what an answer is. The
          // starter cards and the scope sizes are on the desktop empty state; here
          // the field is the invitation.
          <p className="pt-2 text-[12.5px] leading-[1.55] text-microcopy text-pretty">
            Ask about your own life. Answers cite what they read — journal entries, lecture
            transcripts, announcements. If something can’t be cited, it won’t be claimed.
          </p>
        )}

        {!isNew && detail.isPending && <Skeleton height={120} className="rounded-card" />}

        {detail.error && (
          <p className="text-[12px] leading-[1.55] text-microcopy text-pretty">
            This thread could not be read.{' '}
            {detail.error instanceof Error ? detail.error.message : ''}
          </p>
        )}

        {detail.data && detail.data.messages.length === 0 && (
          <p className="text-[12px] leading-[1.55] text-microcopy text-pretty">
            This thread holds {messageCountLine(detail.data.thread.messageCount)} and none of them
            came back. Its scope and its length are real; the messages themselves are not in this
            dataset, and inventing them would mean inventing the sources they cite.
          </p>
        )}

        {detail.data?.messages.map((m) => <Bubble key={m.id} message={m} onWiden={setScope} />)}

        {/* THE WAIT, STATED RATHER THAN PERFORMED. See the header comment: the
            engine returns a complete answer, so there is nothing to stream and
            nothing here pretends there is. */}
        {asking && (
          <div className="max-w-[300px] self-end whitespace-pre-wrap rounded-[14px_14px_4px_14px] bg-ink px-[14px] py-[11px] text-[14px] font-normal leading-[1.55] text-surface text-pretty">
            {asking}
          </div>
        )}

        {send.isPending && (
          <div className="flex gap-[10px]">
            <span className="size-7 flex-none rounded-button border border-accent-line bg-accent-fill" aria-hidden />
            <p className="flex-1 text-[12.5px] leading-[1.55] text-microcopy text-pretty">
              Reading {scope ? (scopeSizeLine(scope.size) ?? 'the corpus') : 'the corpus'} at the{' '}
              {tier} tier, then answering with what it actually read.
            </p>
          </div>
        )}

        {send.error && (
          <p role="status" className="text-[12px] leading-[1.55] text-danger text-pretty">
            {send.error instanceof Error ? send.error.message : 'That question could not be asked.'}
          </p>
        )}

        {send.data?.note && (
          <p className="text-[11.5px] leading-[1.5] text-warning-2 text-pretty">{send.data.note}</p>
        )}

        <div ref={bottom} className="h-1 flex-none" aria-hidden />
      </div>

      {/* ── THE COMPOSER ───────────────────────────────────────────────────── */}
      <div className="flex-none px-[14px] pt-[10px]" style={{ paddingBottom: SCROLL_BOTTOM }}>
        <div className="flex flex-col gap-[10px] rounded-card border border-line-2 bg-surface px-3 py-[11px] shadow-composer">
          <div className="flex items-center gap-[9px]">
            {/* THE SCOPE, NAMED AND ONE TAP FROM CHANGING. `aria-label` carries the
                scope so a test (or a screen reader) can read what this chat is
                over without opening the picker. */}
            <button
              type="button"
              aria-label={`Scope: ${scope?.label ?? 'Everything'}`}
              aria-expanded={scopeOpen}
              onClick={() => setScopeOpen(true)}
              className="flex min-w-0 items-center gap-[7px] rounded-button py-1 pr-1 text-left active:bg-fill"
            >
              <span className={clsx('size-[6px] flex-none rounded-full', SCOPE_DOT[scope?.domain ?? 'everything'])} aria-hidden />
              <span className="min-w-0 truncate text-[11px] font-medium leading-none text-ink-3">
                {scope?.label ?? 'Everything'}
              </span>
              <MChevronDown className="flex-none text-microcopy" />
            </button>
            <div className="ml-auto flex flex-none rounded-button bg-fill-2 p-[3px]">
              {(['quick', 'deep'] as ChatTier[]).map((t) => (
                <button
                  key={t}
                  type="button"
                  aria-pressed={tier === t}
                  // The trade-off the removed footer line used to state, kept where the
                  // choice is made.
                  title={t === 'quick' ? 'Reads titles and summaries' : 'Reads full transcripts; takes longer'}
                  onClick={() => setTier(t)}
                  className={clsx(
                    'rounded-chip px-[11px] py-[5px] text-[11px] leading-none',
                    tier === t
                      ? 'mo-press bg-surface font-semibold text-ink shadow-thumb'
                      : 'font-medium text-muted active:bg-fill',
                  )}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>

          <form
            className="flex items-end gap-[10px]"
            onSubmit={(e) => {
              e.preventDefault()
              ask()
            }}
          >
            {/* Grows with the question, to six lines (owner, 2026-09-20). On a phone the
                keyboard's return key makes a new line and the tile sends — there is no
                Shift to hold, so Enter-to-send would eat every line break. */}
            <textarea
              ref={field}
              rows={1}
              value={draft}
              onChange={(e) => {
                setDraft(e.currentTarget.value)
                fitToText(e.currentTarget, CHAT_INPUT_MAX_PX)
              }}
              placeholder="Ask about your own life…"
              aria-label="Ask about your own life"
              // No `.mo-field` wrapper: the row also holds the quick/deep toggle
              // and the send tile above it, and `:focus-within` on the whole
              // composer card would ring the segmented control too. Dropping
              // `outline-none` lets the global `*:focus-visible` ring land on
              // the input itself instead.
              className="my-[10px] min-w-0 flex-1 resize-none border-0 bg-transparent p-0 text-[14px] font-normal leading-[1.4] text-ink placeholder:text-microcopy"
            />
            {/* Asking by voice is not built — the recording path writes a journal
                memo, not a question. Absent rather than dead: the mic tile the
                mockup draws here is not rendered. */}
            <button
              type="submit"
              aria-label="Send"
              disabled={send.isPending || draft.trim().length === 0}
              className="flex size-10 flex-none items-center justify-center rounded-row bg-ink text-surface active:bg-[color-mix(in_srgb,var(--color-ink),black_30%)] disabled:opacity-40"
            >
              <MSend />
            </button>
          </form>
        </div>
      </div>

      <ThreadDrawer
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        threads={threads.data}
        pending={threads.isPending}
        activeId={activeId}
        onNew={() => {
          setMenuOpen(false)
          navigate('/chat')
        }}
        onOpen={(id) => {
          setMenuOpen(false)
          navigate(`/chat/${id}`)
        }}
      />

      <ScopeSheet
        open={scopeOpen}
        onClose={() => setScopeOpen(false)}
        scopes={scopes.data}
        activeId={scopeId}
        onPick={(id) => {
          setScope(id)
          setScopeOpen(false)
        }}
      />
    </>
  )
}

// ── The two overlays ──────────────────────────────────────────────────────────
//
// Both are `absolute inset-0` against the shell (mobile/MobileApp.tsx's root is
// `relative overflow-hidden`), so they cover the tab bar too, and `z-40` because
// the tab bar is a later sibling in the DOM and would otherwise paint over them.
// A tap on the backdrop closes; nothing else on the page is reachable while one is
// open. Not portalled: the shell is the phone frame, and a portal to `body` would
// escape it in the desktop-browser preview.

/** The thread menu: New chat first, then every earlier thread, newest first. */
function ThreadDrawer({
  open,
  onClose,
  threads,
  pending,
  activeId,
  onNew,
  onOpen,
}: {
  open: boolean
  onClose: () => void
  threads: ChatThread[] | undefined
  pending: boolean
  activeId: string | undefined
  onNew: () => void
  onOpen: (id: string) => void
}) {
  if (!open) return null
  return (
    <div className="absolute inset-0 z-40 flex justify-end" role="dialog" aria-label="Chats">
      <button type="button" aria-label="Close" onClick={onClose} className="absolute inset-0 bg-ink/35" />
      <div
        className="relative flex h-full w-[82%] max-w-[340px] flex-col bg-surface shadow-card"
        style={{ paddingTop: 'env(safe-area-inset-top, 0px)', paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
      >
        <div className="flex flex-none items-center justify-between px-[16px] pb-[6px] pt-[14px]">
          <span className="text-[13px] font-semibold leading-none text-ink">Chats</span>
          <button
            type="button"
            aria-label="Close chats"
            onClick={onClose}
            className="flex size-9 items-center justify-center rounded-row text-ink-3 active:bg-fill"
          >
            <span className="text-[18px] leading-none" aria-hidden>×</span>
          </button>
        </div>

        <button
          type="button"
          onClick={onNew}
          className="mx-[10px] flex flex-none items-center gap-[10px] rounded-row bg-ink px-[12px] py-[11px] text-left text-surface active:bg-[color-mix(in_srgb,var(--color-ink),black_30%)]"
        >
          <MPlus size={18} strokeWidth={2.2} />
          <span className="text-[13px] font-semibold leading-none">New chat</span>
        </button>

        <div className="mt-[10px] flex min-h-0 flex-1 flex-col overflow-y-auto px-[10px] pb-[10px]">
          <Eyebrow className="px-[6px] pb-[6px] pt-[6px] text-[9.5px] text-microcopy">Earlier</Eyebrow>
          {pending && <Skeleton height={52} className="rounded-row" />}
          {threads && threads.length === 0 && (
            <p className="px-[6px] text-[12px] leading-[1.5] text-microcopy">No chats yet.</p>
          )}
          {threads?.map((t) => (
            <button
              key={t.id}
              type="button"
              aria-current={t.id === activeId ? 'page' : undefined}
              onClick={() => onOpen(t.id)}
              className={clsx(
                'flex flex-col items-start gap-[5px] rounded-row px-[10px] py-[10px] text-left active:bg-fill',
                t.id === activeId && 'bg-fill-2',
              )}
            >
              <span className="line-clamp-2 text-[12.5px] font-medium leading-[1.3] text-ink text-pretty">{t.title}</span>
              <span className="text-[10.5px] font-normal leading-none text-microcopy">
                {t.scopeLabel} · {messageCountLine(t.messageCount)}
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}

/**
 * The scope picker, as a bottom sheet. Each row carries the scope's derived size
 * line — the sentence the header used to show for the active scope alone, moved
 * to the one place it helps: choosing.
 */
function ScopeSheet({
  open,
  onClose,
  scopes,
  activeId,
  onPick,
}: {
  open: boolean
  onClose: () => void
  scopes: ChatScope[] | undefined
  activeId: ChatScopeId
  onPick: (id: ChatScopeId) => void
}) {
  if (!open) return null
  return (
    <div className="absolute inset-0 z-40 flex flex-col justify-end" role="dialog" aria-label="Scope">
      <button type="button" aria-label="Close" onClick={onClose} className="absolute inset-0 bg-ink/35" />
      <div
        className="relative flex max-h-[70%] flex-col rounded-t-[18px] bg-surface shadow-card"
        style={{ paddingBottom: 'env(safe-area-inset-bottom, 12px)' }}
      >
        <div className="flex flex-none flex-col gap-[3px] px-[18px] pb-[8px] pt-[14px]">
          <span className="text-[13px] font-semibold leading-none text-ink">What this chat reads</span>
          <span className="text-[11px] leading-[1.4] text-microcopy">
            Answers come only from inside the scope. Changing it applies to your next question.
          </span>
        </div>
        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto px-[10px] pb-[8px]">
          {scopes?.map((s) => {
            const active = s.id === activeId
            return (
              <button
                key={s.id}
                type="button"
                role="option"
                aria-selected={active}
                onClick={() => onPick(s.id)}
                className={clsx(
                  'flex items-center gap-[10px] rounded-row px-[10px] py-[10px] text-left active:bg-fill',
                  active && 'bg-fill-2',
                )}
              >
                <span className={clsx('size-[7px] flex-none rounded-full', SCOPE_DOT[s.domain] ?? 'bg-ink')} aria-hidden />
                <span className="flex min-w-0 flex-1 flex-col gap-[4px]">
                  <span className="text-[12.5px] font-medium leading-none text-ink">{s.label}</span>
                  <span className="text-[10.5px] leading-[1.35] text-microcopy">
                    {scopeSizeLine(s.size) ?? 'nothing in scope yet'}
                  </span>
                </span>
                {active && <MCheck className="flex-none text-ink" />}
              </button>
            )
          })}
        </div>
      </div>
    </div>
  )
}

const SCOPE_DOT: Record<string, string> = {
  everything: 'bg-ink',
  journal: 'bg-journal',
  school: 'bg-school',
  interests: 'bg-interests',
  learning: 'bg-learning',
}

function Bubble({
  message,
  onWiden,
}: {
  message: ChatMessage
  onWiden: (id: ChatScopeId) => void
}) {
  if (message.role === 'user') {
    return (
      <div className="max-w-[300px] self-end rounded-[14px_14px_4px_14px] bg-ink px-[14px] py-[11px] text-[14px] font-normal leading-[1.55] text-surface text-pretty">
        {message.text}
      </div>
    )
  }

  return (
    <div className="flex items-start gap-[10px]">
      <span
        className="flex size-7 flex-none items-center justify-center rounded-button border border-accent-line bg-accent-fill"
        aria-hidden
      >
        <span className="size-[9px] rounded-[2px] bg-chat" />
      </span>
      <div className="flex min-w-0 flex-1 flex-col gap-[11px]">
        <AnswerBody
          text={message.text}
          className="text-[14px] font-normal leading-[1.65] text-ink text-pretty"
        />

        {message.sources.length > 0 ? (
          <div className="flex flex-col gap-[7px]">
            <Eyebrow className="text-[9.5px] text-microcopy">Sources</Eyebrow>
            <div className="-mx-[14px] flex gap-[9px] overflow-x-auto px-[14px] [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              {message.sources.map((s, i) => (
                <SourceCard key={`${s.href}-${i}`} source={s} />
              ))}
            </div>
          </div>
        ) : (
          // A CLAIM WITH NO SOURCE REF IS A BUG, and the design shows it caught.
          <p className="rounded-row border border-danger-line bg-danger-fill px-3 py-[9px] text-[11.5px] leading-[1.5] text-danger text-pretty">
            This answer came back citing nothing, so nothing in it can be checked. Every claim about
            your own life is supposed to carry at least one source — treat this one as unverified.
          </p>
        )}

        {message.scopeLimit && (
          <div className="flex flex-col gap-[9px] rounded-row border border-warning-line bg-warning-fill px-3 py-[11px]">
            <p className="text-[12px] font-normal leading-[1.55] text-warning text-pretty">
              {message.scopeLimit.text}
            </p>
            <button
              type="button"
              onClick={() => onWiden(message.scopeLimit!.widenTo)}
              className="flex h-[34px] flex-none items-center self-start rounded-button border border-warning-line bg-surface px-[14px] text-[12px] font-semibold leading-none text-warning-2 active:bg-warning-fill"
            >
              {message.scopeLimit.widenLabel}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

/**
 * A 198px source card. The row scrolls and ends on a partly visible card, which
 * is the only affordance a horizontal scroll gets on a phone.
 */
function SourceCard({ source }: { source: ChatSource }) {
  const navigate = useNavigate()
  return (
    <button
      type="button"
      onClick={() => navigate(source.href)}
      className="flex w-[198px] flex-none flex-col items-start gap-[7px] rounded-row border border-line bg-surface px-3 py-[11px] text-left active:bg-surface-2"
    >
      <span
        className={clsx(
          'rounded-pill px-[7px] py-[2px] text-[9px] font-semibold uppercase leading-[1.5] tracking-[0.05em]',
          source.kind === 'transcript' ? 'bg-info-fill text-info' : 'bg-fill-2 text-ink-3',
        )}
      >
        {source.pill}
      </span>
      <span className="text-[11.5px] font-medium leading-[1.35] text-ink text-pretty">
        {source.locator}
      </span>
      {source.timestampMs !== null ? (
        <span className="text-[10.5px] font-medium leading-none text-success-dot">
          Jump to timestamp
        </span>
      ) : (
        source.detail && (
          <span className="text-[10.5px] font-normal leading-none text-microcopy">
            {source.detail}
          </span>
        )
      )}
    </button>
  )
}
